import React, { useMemo, useState } from 'react';
import { useData, ESTADOS_CITA } from '../context/DataContext';
import { useAuth, isTecnico } from '../context/AuthContext';
import { useToast } from '../components/Toast';
import Modal from '../components/Modal';
import ServiceTag, { SERVICIOS } from '../components/ServiceTag';

const ESTADO_COLORS = {
  'Reservada':           { background: '#eff6ff', color: '#185fa5' },
  'Confirmada':          { background: '#dbeafe', color: '#1d4ed8' },
  'En servicio':         { background: '#fef9c3', color: '#854d0e' },
  'Completada':          { background: '#dcfce7', color: '#16a34a' },
  'Cancelada x cliente': { background: '#fee2e2', color: '#dc2626' },
  'Cancelada x empresa': { background: '#fee2e2', color: '#991b1b' },
  'No asistió':          { background: '#f1f5f9', color: '#475569' }
};

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const DIAS = ['Lun','Mar','Mié','Jue','Vie','Sáb','Dom'];

function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function parseYmd(s) {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export default function Agenda() {
  const { citas, addCita, updateCita, removeCita } = useData();
  const { users } = useAuth();
  const toast = useToast();

  const [vista, setVista] = useState('mes'); // dia | semana | mes
  const [cursor, setCursor] = useState(new Date());
  const [editCita, setEditCita] = useState(null);
  const [menuCita, setMenuCita] = useState(null); // {cita, x, y}
  const [nuevaCitaFecha, setNuevaCitaFecha] = useState(null);

  const tecnicos = users.filter(u => isTecnico(u.rol));
  const mostradores = users.filter(u => u.rol === 'Mostrador' || u.rol === 'Administrador');

  const citasPorFecha = useMemo(() => {
    const map = {};
    citas.forEach(c => {
      if (!c.fecha) return;
      (map[c.fecha] = map[c.fecha] || []).push(c);
    });
    Object.values(map).forEach(arr => arr.sort((a, b) => (a.hora || '').localeCompare(b.hora || '')));
    return map;
  }, [citas]);

  const irACitas = () => {
    const conFecha = citas.filter(c => c.fecha).map(c => c.fecha).sort();
    if (!conFecha.length) { toast.show('No hay citas registradas', 'error'); return; }
    const hoy = ymd(new Date());
    const futura = conFecha.find(f => f >= hoy) || conFecha[conFecha.length - 1];
    setCursor(parseYmd(futura));
    setVista('mes');
  };

  const mover = (delta) => {
    const d = new Date(cursor);
    if (vista === 'dia') d.setDate(d.getDate() + delta);
    if (vista === 'semana') d.setDate(d.getDate() + delta * 7);
    if (vista === 'mes') d.setMonth(d.getMonth() + delta);
    setCursor(d);
  };

  // ── chips de cita ──
  const Chip = ({ cita }) => {
    const col = ESTADO_COLORS[cita.estado] || ESTADO_COLORS['Reservada'];
    return (
      <div
        onClick={(e) => { e.stopPropagation(); setMenuCita({ cita, x: e.clientX, y: e.clientY }); }}
        onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); setMenuCita({ cita, x: e.clientX, y: e.clientY }); }}
        title={`${cita.nombre} — ${cita.estado}`}
        style={{
          ...col, padding: '2px 7px', borderRadius: 6, fontSize: 11.5, fontWeight: 600,
          marginBottom: 3, cursor: 'pointer', overflow: 'hidden',
          textOverflow: 'ellipsis', whiteSpace: 'nowrap'
        }}
      >
        {cita.hora ? cita.hora + ' · ' : ''}{cita.nombre}
      </div>
    );
  };

  // ── vista MES ──
  const renderMes = () => {
    const y = cursor.getFullYear(), m = cursor.getMonth();
    const first = new Date(y, m, 1);
    const offset = (first.getDay() + 6) % 7; // lunes = 0
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < offset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(y, m, d));
    const hoy = ymd(new Date());

    return (
      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', borderBottom: '1px solid #e2e8f0' }}>
          {DIAS.map(d => (
            <div key={d} style={{ padding: 8, fontSize: 12, fontWeight: 700, color: '#475569', textAlign: 'center' }}>{d}</div>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)' }}>
          {cells.map((d, i) => {
            const key = d ? ymd(d) : null;
            const lista = key ? (citasPorFecha[key] || []) : [];
            return (
              <div
                key={i}
                onClick={() => d && setNuevaCitaFecha(key)}
                style={{
                  minHeight: 92, padding: 6, borderRight: '1px solid #f1f5f9',
                  borderBottom: '1px solid #f1f5f9', cursor: d ? 'pointer' : 'default',
                  background: key === hoy ? '#eff6ff' : '#fff'
                }}
              >
                {d && (
                  <>
                    <div style={{
                      fontSize: 12, fontWeight: 700, marginBottom: 4,
                      color: key === hoy ? '#185fa5' : '#94a3b8'
                    }}>{d.getDate()}</div>
                    {lista.slice(0, 3).map(c => <Chip key={c.id} cita={c} />)}
                    {lista.length > 3 && (
                      <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>+{lista.length - 3} más</div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ── vista SEMANA ──
  const renderSemana = () => {
    const start = new Date(cursor);
    start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
    const dias = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start); d.setDate(d.getDate() + i); return d;
    });
    const hoy = ymd(new Date());
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 10 }}>
        {dias.map(d => {
          const key = ymd(d);
          const lista = citasPorFecha[key] || [];
          return (
            <div key={key} className="card" style={{ padding: 10, minHeight: 160, cursor: 'pointer', outline: key === hoy ? '2px solid #185fa5' : 'none' }}
              onClick={() => setNuevaCitaFecha(key)}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 6 }}>
                {DIAS[(d.getDay() + 6) % 7]} {d.getDate()}
              </div>
              {lista.map(c => <Chip key={c.id} cita={c} />)}
              {!lista.length && <div style={{ fontSize: 12, color: '#cbd5e1' }}>Sin citas</div>}
            </div>
          );
        })}
      </div>
    );
  };

  // ── vista DÍA ──
  const renderDia = () => {
    const key = ymd(cursor);
    const lista = citasPorFecha[key] || [];
    return (
      <div className="card" style={{ padding: 16 }}>
        {!lista.length && <div style={{ color: '#94a3b8', fontSize: 14 }}>Sin citas este día. Haz clic en "+ Nueva cita" para agregar.</div>}
        {lista.map(c => {
          const col = ESTADO_COLORS[c.estado] || ESTADO_COLORS['Reservada'];
          return (
            <div key={c.id}
              onClick={(e) => setMenuCita({ cita: c, x: e.clientX, y: e.clientY })}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
                borderBottom: '1px solid #f1f5f9', cursor: 'pointer', flexWrap: 'wrap'
              }}>
              <div style={{ fontWeight: 700, width: 52 }}>{c.hora || '--:--'}</div>
              <div style={{ fontWeight: 600, flex: 1, minWidth: 140 }}>{c.nombre}</div>
              <ServiceTag servicio={c.servicio} />
              <span style={{ ...col, padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600 }}>{c.estado}</span>
            </div>
          );
        })}
      </div>
    );
  };

  const titulo = vista === 'mes'
    ? `${MESES[cursor.getMonth()]} ${cursor.getFullYear()}`
    : vista === 'semana'
      ? `Semana del ${cursor.getDate()} de ${MESES[cursor.getMonth()]}`
      : cursor.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 20, fontWeight: 800, marginRight: 'auto' }}>Agenda</h1>
        <button className="btn-secondary" onClick={irACitas}>📍 Ir a citas</button>
        <button className="btn-primary" onClick={() => setNuevaCitaFecha(ymd(cursor))}>+ Nueva cita</button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <button className="btn-secondary" onClick={() => mover(-1)}>‹</button>
        <div style={{ fontWeight: 700, fontSize: 15, minWidth: 180, textTransform: 'capitalize' }}>{titulo}</div>
        <button className="btn-secondary" onClick={() => mover(1)}>›</button>
        <button className="btn-secondary" onClick={() => setCursor(new Date())}>Hoy</button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 3 }}>
          {['dia', 'semana', 'mes'].map(v => (
            <button key={v} onClick={() => setVista(v)} style={{
              border: 'none', borderRadius: 6, padding: '6px 12px', fontSize: 13, fontWeight: 600,
              background: vista === v ? '#185fa5' : 'transparent',
              color: vista === v ? '#fff' : '#475569', textTransform: 'capitalize'
            }}>{v === 'dia' ? 'Día' : v}</button>
          ))}
        </div>
      </div>

      {vista === 'mes' && renderMes()}
      {vista === 'semana' && renderSemana()}
      {vista === 'dia' && renderDia()}

      {/* menú contextual */}
      {menuCita && (
        <div onClick={() => setMenuCita(null)} style={{ position: 'fixed', inset: 0, zIndex: 290 }}>
          <div
            onClick={e => e.stopPropagation()}
            className="card"
            style={{
              position: 'fixed',
              left: Math.min(menuCita.x, window.innerWidth - 230),
              top: Math.min(menuCita.y, window.innerHeight - 360),
              width: 215, padding: 6, zIndex: 295
            }}
          >
            <div style={{ padding: '6px 10px', fontWeight: 700, fontSize: 13, borderBottom: '1px solid #f1f5f9', marginBottom: 4 }}>
              {menuCita.cita.nombre}
            </div>
            {ESTADOS_CITA.map(est => (
              <button key={est}
                onClick={() => { updateCita(menuCita.cita.id, { estado: est }); setMenuCita(null); toast.show('Estado: ' + est); }}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px',
                  background: menuCita.cita.estado === est ? '#eff6ff' : 'transparent',
                  border: 'none', borderRadius: 6, fontSize: 13,
                  color: menuCita.cita.estado === est ? '#185fa5' : '#0f172a',
                  fontWeight: menuCita.cita.estado === est ? 700 : 500
                }}>
                {est}
              </button>
            ))}
            <div style={{ borderTop: '1px solid #f1f5f9', marginTop: 4, paddingTop: 4 }}>
              <button onClick={() => { setEditCita({ ...menuCita.cita }); setMenuCita(null); }}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', background: 'transparent', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, color: '#185fa5' }}>
                ✏️ Editar cita
              </button>
              {!menuCita.cita.auto && (
                <button onClick={() => { removeCita(menuCita.cita.id); setMenuCita(null); toast.show('Cita eliminada'); }}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', background: 'transparent', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, color: '#dc2626' }}>
                  🗑 Eliminar
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* modal editar */}
      {editCita && (
        <Modal title="Editar cita" onClose={() => setEditCita(null)}>
          <CitaForm
            cita={editCita}
            tecnicos={tecnicos}
            mostradores={mostradores}
            onSave={(data) => { updateCita(editCita.id, data); setEditCita(null); toast.show('Cita actualizada'); }}
          />
        </Modal>
      )}

      {/* modal nueva */}
      {nuevaCitaFecha && (
        <Modal title="Nueva cita" onClose={() => setNuevaCitaFecha(null)}>
          <CitaForm
            cita={{ fecha: nuevaCitaFecha, hora: '', nombre: '', servicio: '', estado: 'Reservada', atiende: '', tecnico: '', notas: '' }}
            tecnicos={tecnicos}
            mostradores={mostradores}
            onSave={(data) => {
              if (!data.nombre.trim()) { toast.show('El nombre es obligatorio', 'error'); return; }
              addCita(data); setNuevaCitaFecha(null); toast.show('Cita creada');
            }}
          />
        </Modal>
      )}
    </div>
  );
}

function CitaForm({ cita, tecnicos, mostradores, onSave }) {
  const [f, setF] = useState(cita);
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  const L = ({ children }) => <label style={{ fontSize: 13, fontWeight: 600, color: '#475569', display: 'block', margin: '10px 0 5px' }}>{children}</label>;

  return (
    <div>
      <L>Cliente</L>
      <input type="text" value={f.nombre} onChange={e => set('nombre', e.target.value)} disabled={!!cita.auto} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <L>Fecha</L>
          <input type="date" value={f.fecha} onChange={e => set('fecha', e.target.value)} />
        </div>
        <div>
          <L>Hora</L>
          <input type="time" value={f.hora} onChange={e => set('hora', e.target.value)} />
        </div>
      </div>
      <L>Tipo de servicio</L>
      <select value={f.servicio} onChange={e => set('servicio', e.target.value)}>
        <option value="">— Seleccionar —</option>
        {SERVICIOS.map(s => <option key={s} value={s}>{s}</option>)}
        {f.servicio && !SERVICIOS.includes(f.servicio) && <option value={f.servicio}>{f.servicio}</option>}
      </select>
      <L>Estado</L>
      <select value={f.estado} onChange={e => set('estado', e.target.value)}>
        {ESTADOS_CITA.map(s => <option key={s} value={s}>{s}</option>)}
      </select>
      <L>Atendió en Mostrador</L>
      <select value={f.atiende || ''} onChange={e => set('atiende', e.target.value)}>
        <option value="">— Seleccionar —</option>
        {mostradores.map(u => <option key={u.id} value={u.nombre}>{u.nombre}</option>)}
        {f.atiende && !mostradores.some(u => u.nombre === f.atiende) && <option value={f.atiende}>{f.atiende}</option>}
      </select>
      <L>Técnico que atendió</L>
      <select value={f.tecnico || ''} onChange={e => set('tecnico', e.target.value)}>
        <option value="">— Seleccionar —</option>
        {tecnicos.map(u => <option key={u.id} value={u.nombre}>{u.nombre} ({u.rol})</option>)}
        {f.tecnico && !tecnicos.some(u => u.nombre === f.tecnico) && <option value={f.tecnico}>{f.tecnico}</option>}
      </select>
      <L>Notas</L>
      <textarea rows={3} value={f.notas || ''} onChange={e => set('notas', e.target.value)} />
      <button className="btn-primary" style={{ width: '100%', marginTop: 16 }} onClick={() => onSave(f)}>
        Guardar
      </button>
    </div>
  );
}
